import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { getFamilyHistoryIntake } from '../services/familyHistoryIntakeService';
import { listObservationsForUser } from '../services/observationService';
import {
  createMenstrualCycleEntry,
  deleteMenstrualCycleEntry,
  listMenstrualCycleEntries,
} from '../services/menstrualCycleService';
import type { ObservationRecord } from '../types/observations';
import type { MenstrualCycleEntryRecord } from '../types/menstrualCycle';

function formatDate(value?: string | null) {
  if (!value) return 'Date not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function toDateKey(value?: string | null) {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function daysBetween(start: string, end: string) {
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return 0;
  return Math.max(0, Math.round((endTime - startTime) / (24 * 60 * 60 * 1000)));
}

function getCurrentCycleDay(entries: MenstrualCycleEntryRecord[]) {
  const latest = [...entries]
    .sort((left, right) => right.periodStartDate.localeCompare(left.periodStartDate))[0];
  if (!latest?.periodStartDate) return null;
  return daysBetween(latest.periodStartDate, new Date().toISOString().slice(0, 10)) + 1;
}

function normalizeSeries(values: number[]) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return values.map(() => 0.5);
  return values.map((value) => (value - min) / (max - min));
}

function buildOverlayData(observations: ObservationRecord[]) {
  const grouped = new Map<string, {
    observedAt: string;
    skinTemperature?: number;
    restingHeartRate?: number;
    hrv?: number;
    sleepDuration?: number;
  }>();

  observations.forEach((observation) => {
    const dateKey = toDateKey(observation.observedAt);
    const display = String(observation.display || '').toLowerCase();
    const code = String(observation.code || '').toLowerCase();
    const value = observation.valueNumber;
    if (!dateKey || value == null || Number.isNaN(value)) return;
    const entry = grouped.get(dateKey) || { observedAt: observation.observedAt || `${dateKey}T00:00:00Z` };

    if (display.includes('skin temp') || display.includes('skin temperature') || code.includes('skin_temperature') || code.includes('skin_temp')) entry.skinTemperature = value;
    if (display.includes('resting heart rate') || display === 'heart rate' || display === 'resting hr' || code.includes('resting_heart_rate') || code.includes('heart_rate')) entry.restingHeartRate = value;
    if (display.includes('heart rate variability') || display === 'hrv' || code.includes('heart_rate_variability') || code === 'hrv') entry.hrv = value;
    if (display.includes('asleep duration') || display.includes('sleep duration') || code.includes('sleep_duration')) entry.sleepDuration = observation.unit === 'min' ? value / 60 : value;

    grouped.set(dateKey, entry);
  });

  return Array.from(grouped.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .slice(-30)
    .map(([dateKey, entry]) => ({
      dateKey,
      label: formatDate(dateKey).replace(/\s\d{4}$/, ''),
      observedAt: entry.observedAt,
      skinTemperature: entry.skinTemperature ?? null,
      restingHeartRate: entry.restingHeartRate ?? null,
      hrv: entry.hrv ?? null,
      sleepDuration: entry.sleepDuration ?? null,
    }));
}

function CycleOverlayChart({
  observations,
  entries,
}: {
  observations: ObservationRecord[];
  entries: MenstrualCycleEntryRecord[];
}) {
  const overlayData = buildOverlayData(observations);

  if (!overlayData.length) {
    return <div className="h-64 flex items-center justify-center text-sm" style={{ color: 'var(--melio-text-muted)' }}>No parsed wearable data yet.</div>;
  }

  const width = 920;
  const height = 320;
  const left = 42;
  const top = 18;
  const right = 24;
  const bottom = 34;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;

  const series = [
    {
      key: 'skinTemperature',
      label: 'Skin temp',
      color: '#C77B63',
      values: overlayData.map((point) => point.skinTemperature ?? NaN),
    },
    {
      key: 'restingHeartRate',
      label: 'Resting HR',
      color: '#4F5D18',
      values: overlayData.map((point) => point.restingHeartRate ?? NaN),
    },
    {
      key: 'hrv',
      label: 'HRV',
      color: '#547092',
      values: overlayData.map((point) => point.hrv ?? NaN),
    },
    {
      key: 'sleepDuration',
      label: 'Sleep duration',
      color: '#A98363',
      values: overlayData.map((point) => point.sleepDuration ?? NaN),
    },
  ] as const;

  const normalizedSeries = series.map((seriesItem) => {
    const validValues = seriesItem.values.filter((value) => Number.isFinite(value));
    const normalizedValues = validValues.length > 0 ? normalizeSeries(validValues as number[]) : [];
    let validIndex = 0;
    return {
      ...seriesItem,
      normalizedValues: seriesItem.values.map((value) => {
        if (!Number.isFinite(value)) return null;
        const normalized = normalizedValues[validIndex];
        validIndex += 1;
        return normalized;
      }),
    };
  });

  const shadedWindows = entries.map((entry) => {
    const startIndex = overlayData.findIndex((point) => point.dateKey >= entry.periodStartDate);
    const endIndex = overlayData.findIndex((point) => point.dateKey > entry.periodEndDate);
    const normalizedEndIndex = endIndex === -1 ? overlayData.length : endIndex;
    if (startIndex === -1) return null;
    return { startIndex, endIndex: normalizedEndIndex };
  }).filter(Boolean) as Array<{ startIndex: number; endIndex: number }>;

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-72" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((tick) => {
          const y = top + (tick / 4) * chartHeight;
          return (
            <line
              key={tick}
              x1={left}
              y1={y}
              x2={width - right}
              y2={y}
              stroke="rgba(26, 26, 24,0.08)"
              strokeWidth="1"
            />
          );
        })}

        {shadedWindows.map((window, index) => {
          const startX = left + ((window.startIndex + 0.15) / overlayData.length) * chartWidth;
          const endX = left + ((window.endIndex + 0.15) / overlayData.length) * chartWidth;
          return (
            <rect
              key={`${window.startIndex}-${window.endIndex}-${index}`}
              x={startX}
              y={top}
              width={Math.max(12, endX - startX)}
              height={chartHeight}
              fill="rgba(199, 123, 99, 0.14)"
              rx="10"
            />
          );
        })}

        {normalizedSeries.map((seriesItem) => {
          const validPoints = overlayData
            .map((point, index) => {
              const normalized = seriesItem.normalizedValues[index];
              if (normalized == null) return null;
              const x = left + ((index + 0.5) / overlayData.length) * chartWidth;
              const y = top + (1 - normalized) * chartHeight;
              return { x, y, key: point.dateKey };
            })
            .filter(Boolean) as Array<{ x: number; y: number; key: string }>;

          if (!validPoints.length) return null;
          const path = validPoints.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(' ');
          return (
            <g key={seriesItem.key}>
              <path d={path} fill="none" stroke={seriesItem.color} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
              {validPoints.map((point) => (
                <circle key={point.key} cx={point.x} cy={point.y} r="3.6" fill={seriesItem.color} />
              ))}
            </g>
          );
        })}

        {overlayData.map((point, index) => {
          const x = left + ((index + 0.5) / overlayData.length) * chartWidth;
          return (
            <text key={point.dateKey} x={x} y={height - 10} textAnchor="middle" fontSize="10.5" fill="var(--melio-text-muted)">
              {index === 0 || index === overlayData.length - 1 || index === Math.floor(overlayData.length / 2) ? point.label : ''}
            </text>
          );
        })}
      </svg>

      <div className="flex flex-wrap gap-4 mt-4">
        {series.map((seriesItem) => (
          <div key={seriesItem.key} className="inline-flex items-center gap-2 text-xs" style={{ color: 'var(--melio-text-muted)' }}>
            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: seriesItem.color }} />
            {seriesItem.label}
          </div>
        ))}
        <div className="inline-flex items-center gap-2 text-xs" style={{ color: 'var(--melio-text-muted)' }}>
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(199, 123, 99, 0.28)' }} />
          Period window
        </div>
      </div>
    </div>
  );
}

export default function MenstrualCyclePage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState<MenstrualCycleEntryRecord[]>([]);
  const [observations, setObservations] = useState<ObservationRecord[]>([]);
  const [sexAtBirth, setSexAtBirth] = useState<string>('');
  const [form, setForm] = useState({
    periodStartDate: '',
    periodEndDate: '',
    symptoms: '',
  });

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const [intake, cycleEntries, wearableObservations] = await Promise.all([
          getFamilyHistoryIntake(currentUser.uid, { email: currentUser.email, displayName: currentUser.displayName }),
          listMenstrualCycleEntries(currentUser.uid),
          listObservationsForUser(currentUser.uid, 'wearable'),
        ]);
        setSexAtBirth(String(intake.personalInfo.sexAtBirth || '').trim().toLowerCase());
        setEntries(cycleEntries);
        setObservations(wearableObservations);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentUser]);

  const currentCycleDay = useMemo(() => getCurrentCycleDay(entries), [entries]);
  const latestEntry = useMemo(() => [...entries].sort((left, right) => right.periodStartDate.localeCompare(left.periodStartDate))[0] || null, [entries]);
  const isFemaleMember = sexAtBirth === 'female';
  const hasSexRecorded = Boolean(sexAtBirth);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentUser || !form.periodStartDate || !form.periodEndDate) return;
    setSaving(true);
    try {
      await createMenstrualCycleEntry(currentUser.uid, form);
      const refreshed = await listMenstrualCycleEntries(currentUser.uid);
      setEntries(refreshed);
      setForm({ periodStartDate: '', periodEndDate: '', symptoms: '' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId?: string) => {
    if (!currentUser || !entryId) return;
    await deleteMenstrualCycleEntry(currentUser.uid, entryId);
    const refreshed = await listMenstrualCycleEntries(currentUser.uid);
    setEntries(refreshed);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <section className="rounded-3xl border p-8 md:p-10 mb-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
              Cycle-aware tracking
            </p>
            <h1 className="text-3xl md:text-4xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
              Menstrual cycle and temperature context
            </h1>
            <p className="text-sm md:text-base leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
              Log period dates manually, then Melio can place skin temperature, resting heart rate, HRV, and sleep in a cycle-aware view.
            </p>
          </div>
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
          >
            Back to dashboard
          </Link>
        </div>
      </section>

      {!hasSexRecorded ? (
        <section className="rounded-3xl border p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
          <h2 className="text-2xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
            Add sex at birth to enable the right experience
          </h2>
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--melio-text-muted)' }}>
            Melio currently reads this from your history intake. Once that is filled in, cycle-aware tracking can be shown only where relevant.
          </p>
          <Link to="/history" className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
            Open history intake
          </Link>
        </section>
      ) : !isFemaleMember ? (
        <section className="rounded-3xl border p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
          <h2 className="text-2xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
            Cycle tracking is not shown for your current profile
          </h2>
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--melio-text-muted)' }}>
            Skin temperature remains available as a wearable metric on the dashboard for all members, including men. This menstrual-cycle view is only shown when sex at birth is recorded as female.
          </p>
          <Link to="/dashboard" className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
            Return to dashboard metrics
          </Link>
        </section>
      ) : (
        <>
          <section className="grid lg:grid-cols-[0.95fr_1.05fr] gap-6 mb-8">
            <div className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                Current cycle
              </p>
              <h2 className="text-2xl font-normal mb-4" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
                {currentCycleDay ? `Cycle day ${currentCycleDay}` : 'No cycle logged yet'}
              </h2>
              <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--melio-text-muted)' }}>
                {latestEntry
                  ? `Latest period window: ${formatDate(latestEntry.periodStartDate)} to ${formatDate(latestEntry.periodEndDate)}`
                  : 'Log your first period window to anchor the overlay view.'}
              </p>
              {latestEntry?.symptoms && (
                <div className="rounded-2xl p-4" style={{ backgroundColor: 'rgba(199, 123, 99, 0.08)' }}>
                  <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                    Latest symptoms
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-charcoal)' }}>
                    {latestEntry.symptoms}
                  </p>
                </div>
              )}
            </div>

            <form
              onSubmit={handleSubmit}
              className="rounded-3xl border p-6 md:p-8 bg-white"
              style={{ borderColor: 'var(--melio-border)' }}
            >
              <h2 className="text-2xl font-normal mb-4" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
                Log a period window
              </h2>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <label className="block">
                  <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Period start date</span>
                  <input
                    type="date"
                    value={form.periodStartDate}
                    onChange={(event) => setForm((current) => ({ ...current, periodStartDate: event.target.value }))}
                    className="w-full rounded-xl border px-4 py-3 text-sm"
                    style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Period end date</span>
                  <input
                    type="date"
                    value={form.periodEndDate}
                    onChange={(event) => setForm((current) => ({ ...current, periodEndDate: event.target.value }))}
                    className="w-full rounded-xl border px-4 py-3 text-sm"
                    style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                  />
                </label>
              </div>
              <label className="block mb-5">
                <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Optional symptoms</span>
                <textarea
                  value={form.symptoms}
                  onChange={(event) => setForm((current) => ({ ...current, symptoms: event.target.value }))}
                  rows={4}
                  placeholder="e.g. cramps, bloating, lower energy, headaches"
                  className="w-full rounded-xl border px-4 py-3 text-sm"
                  style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                />
              </label>
              <button
                type="submit"
                disabled={saving || !form.periodStartDate || !form.periodEndDate || form.periodEndDate < form.periodStartDate}
                className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
              >
                {saving ? 'Saving...' : 'Save cycle entry'}
              </button>
            </form>
          </section>

          <section className="rounded-3xl border p-6 md:p-8 bg-white mb-8" style={{ borderColor: 'var(--melio-border)' }}>
            <div className="flex items-center justify-between gap-4 mb-5">
              <div>
                <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                  Overlay view
                </p>
                <h2 className="text-2xl font-normal" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
                  Temperature and cycle-aware wearable patterns
                </h2>
              </div>
            </div>
            <CycleOverlayChart observations={observations} entries={entries} />
            <p className="text-sm leading-relaxed mt-4" style={{ color: 'var(--melio-text-muted)' }}>
              This view overlays skin temperature, resting heart rate, HRV, and sleep duration with logged period windows. It is intended for pattern recognition and context, not fertility prediction or diagnosis.
            </p>
          </section>

          <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
            <h2 className="text-2xl font-normal mb-4" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
              Logged period windows
            </h2>
            {entries.length === 0 ? (
              <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
                No cycle entries yet.
              </p>
            ) : (
              <div className="space-y-3">
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="rounded-2xl border p-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
                    style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}
                  >
                    <div>
                      <div className="font-medium mb-1" style={{ color: 'var(--melio-charcoal)' }}>
                        {formatDate(entry.periodStartDate)} to {formatDate(entry.periodEndDate)}
                      </div>
                      {entry.symptoms && (
                        <div className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
                          {entry.symptoms}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(entry.id)}
                      className="inline-flex items-center justify-center px-3 py-2 rounded text-sm font-medium transition-opacity hover:opacity-90"
                      style={{ backgroundColor: '#EFEEE9', color: 'var(--melio-charcoal)' }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
